import { forwardRef } from "react";
import { brl } from "@/lib/api";
import { Button, Thumb } from "@/components/ui-kit";
import { ScanBarcode, Search, Camera } from "lucide-react";

export const isBarcode = (v) => /^\d{8,}$/.test(String(v).trim());

/**
 * Entrada do balcão: leitor de código de barras e busca por nome.
 *
 * Mora na COLUNA DO CARRINHO, e não mais numa faixa acima das duas colunas.
 * Antes, uma busca que devolvia seis produtos com muitas variações empurrava a
 * tela inteira: o fechamento (cliente, pagamento, total) descia junto e o
 * vendedor perdia de vista o botão de finalizar. Agora os resultados empurram só
 * a "Venda atual", que é o que pode descer sem prejuízo — o fechamento fica
 * parado na coluna ao lado.
 *
 * Como a coluna é mais estreita que a tela inteira, a grade cai para duas
 * colunas de resultado (era três) — o suficiente para comparar variações sem
 * espremer o nome do produto em três palavras por linha.
 */
const ProductSearch = forwardRef(function ProductSearch(
  { term, onTermChange, onSubmit, results, onAdd, onScan },
  ref,
) {
  return (
    <div className="bg-white border-2 border-brand-600 rounded-xl p-3 sm:p-4 shrink-0">
      <form onSubmit={onSubmit} className="flex items-center gap-2 sm:gap-3">
        <span className="relative shrink-0">
          <ScanBarcode size={24} className="text-brand-600" />
          <Search size={12} className="absolute -bottom-1 -right-1 text-brand-400 bg-white rounded-full" />
        </span>
        {/* `text-base` no celular não é estética: abaixo de 16px o iOS dá zoom no
            campo ao focar, e o balcão fica com a tela deslocada a cada leitura. */}
        <input ref={ref} value={term} onChange={(e) => onTermChange(e.target.value)}
          aria-label="Código de barras ou nome do produto"
          placeholder="Escaneie o código ou busque por nome..." data-testid="pos-barcode-input"
          className={`flex-1 min-w-0 text-base sm:text-lg outline-none ${isBarcode(term) ? "font-mono" : ""}`} />
        <Button type="button" variant="secondary" size="icon" onClick={onScan}
          data-testid="pos-scan-btn" aria-label="Ler com a câmera"><Camera size={20} /></Button>
        <Button type="submit" data-testid="pos-barcode-submit">Add</Button>
      </form>

      {results.length > 0 && (
        /* Sem teto de altura: os resultados EMPURRAM a venda atual para baixo e
           a página rola. O teto existia porque o PDV inteiro cabia numa tela
           travada, e sem ele a busca esmagava a coluna de fechamento. Com o
           fechamento em coluna própria e altura própria, não há mais o que
           esmagar — e limitar a altura só obrigava a rolar dentro de uma caixa
           de poucos centímetros para achar a variação certa. */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4 pt-4 border-t border-slate-100"
          data-testid="pos-search-results">
          {results.map((p) => (
            <div key={p.id} className="border border-slate-100 rounded-lg p-3">
              {/* A foto ao lado do nome: dois produtos com nomes parecidos
                  ("Órtese para polegar P/M" e "…curta P/M") se distinguem pela
                  foto antes de pela leitura — e no balcão, com o cliente
                  esperando, é a foto que o vendedor olha. */}
              <div className="flex items-start gap-3">
                <Thumb src={p.image} alt="" iconSize={16}
                  className="w-12 h-12 rounded-md border border-slate-100 object-contain bg-white shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">{p.name}</p>
                  {/* A marca vem antes do preço: no balcão o cliente chega pedindo
                      "a muleta da Mercur", e sem ela o vendedor tinha de abrir o
                      cadastro do produto para conferir se era a marca certa. */}
                  <p className="text-xs text-slate-500 mt-0.5">
                    {p.brand && <span className="font-medium">{p.brand} · </span>}
                    <span className="font-mono">{brl(p.price)}</span>
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {p.variants.map((v) => (
                  /* h-9: alvo de toque. Com 32px o dedo acertava a variação
                     vizinha, e no PDV isso vira produto errado no cupom. */
                  /* Sem saldo NÃO desabilita: o balcão vende o que está na
                     prateleira, e o cadastro costuma andar atrás da entrada da
                     mercadoria. O botão fica marcado em âmbar — a informação é
                     "o cadastro está devendo", não "não pode vender". */
                  <button key={v.id} type="button" onClick={() => onAdd(p, v)}
                    data-testid="pos-variant-add-btn" aria-label={`Adicionar ${p.name} ${v.label}`}
                    className={`text-xs border rounded-md px-2 h-9 hover:border-brand-500 ${
                      v.stock <= 0 ? "border-amber-300 bg-amber-50" : "border-slate-200"}`}>
                    {v.label}{" "}
                    <span className={`font-mono ${v.stock <= 0 ? "text-amber-700" : "text-slate-400"}`}>
                      ({v.stock} em estoque)
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {term && !isBarcode(term) && results.length === 0 && (
        <p className="text-xs text-slate-400 mt-3">Nenhum produto encontrado para “{term}”.</p>
      )}
    </div>
  );
});

export default ProductSearch;
